import React, { useEffect, useMemo } from 'react';
import { Box, ButtonV2, DialogV2, TextV2 } from '@withjoy/joykit';
import { ReactComponent as ShippingIcon } from '@assets/icons/fast-shipping.svg';
import { useTranslation } from '@shared/core';
import { getReminderWarningLevel, WarningLevel } from '@apps/registry/common/utils/shippingAddressWarning';
import { useCookedProductList } from '../../selectors/ProductListSelector';
import type { AdminRegistryList, RegistryOrderList, RegistryList } from '../../state/RegistryProducts';
import type { DateFragment } from '@graphql/fragments/coreTypeDefs.graphql.generated';
import { WrapperStyled, styles } from './AddressReminderDialog.styles';
import { useShippingAddressTelemetry } from '../ShippingAddressAndCurrencyDialogsProvider/ShippingAddress.telemetry';

interface AddressReminderDialogProps
  extends Readonly<{
    isOpen: boolean;
    onClose: () => void;
    onActionClick: () => void;
    finalizedEventDate: Maybe<DateFragment>;
    registries?: AdminRegistryList | RegistryList | null;
    orders?: RegistryOrderList;
    eventId: string;
  }> {}

export const AddressReminderDialog = ({ isOpen, onActionClick, onClose, orders, registries, finalizedEventDate, eventId }: AddressReminderDialogProps) => {
  const cookedProducts = useCookedProductList(registries || [], orders, 'everything');
  const { addressReminderDialogOpened } = useShippingAddressTelemetry({ addToContext: { eventId } });
  const { t } = useTranslation('sharedRegistry');
  const {
    reminderTitleLow,
    reminderTitleModerateLine1,
    reminderTitleModerateLine2,
    reminderTitleSevere,
    reminderSubtitleLowModerateLine1,
    reminderSubtitleLowModerateLine2,
    reminderSubtitleSevereLine1,
    reminderSubtitleSevereLine2,
    primaryButton,
  } = t('addressReminder');

  useEffect(() => {
    if (isOpen) {
      addressReminderDialogOpened();
    }
  }, [addressReminderDialogOpened, isOpen]);

  const warningLevel: WarningLevel = useMemo(() => {
    return getReminderWarningLevel({
      deliverableProducts: cookedProducts.counts.deliverable,
      finalizedEventDate,
    });
  }, [finalizedEventDate, cookedProducts.counts.deliverable]);

  const titleLine1 = warningLevel === WarningLevel.Low ? reminderTitleLow() : warningLevel === WarningLevel.Moderate ? reminderTitleModerateLine1() : reminderTitleSevere();
  const titleLine2 = warningLevel === WarningLevel.Moderate ? reminderTitleModerateLine2() : null;
  const subtitleLine1 = warningLevel === WarningLevel.Severe ? reminderSubtitleSevereLine1() : reminderSubtitleLowModerateLine1();
  const subtitleLine2 = warningLevel === WarningLevel.Severe ? reminderSubtitleSevereLine2() : reminderSubtitleLowModerateLine2();

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
    >
      <DialogV2.Header>
        <DialogV2.CloseButton color="black" />
      </DialogV2.Header>
      <DialogV2.Body>
        <WrapperStyled __css={styles.wrapper}>
          <Box marginBottom={28}>
            <ShippingIcon />
          </Box>
          <Box marginBottom={18}>
            <TextV2 typographyVariant="hed5">{titleLine1}</TextV2>
            {titleLine2 && <TextV2 typographyVariant="hed5">{titleLine2}</TextV2>}
          </Box>
          <Box marginBottom={8}>
            <TextV2 typographyVariant="body3">{subtitleLine1}</TextV2>
            {subtitleLine2 && <TextV2 typographyVariant="body3">{subtitleLine2}</TextV2>}
          </Box>
          <ButtonV2
            onClick={onActionClick}
            intent="neutral"
            alignItems="center"
            shape="rounded"
          >
            {primaryButton()}
          </ButtonV2>
        </WrapperStyled>
      </DialogV2.Body>
    </DialogV2>
  );
};
